import { Component, OnInit, signal, inject } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Params, QueryParamsHandling, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { pick, pickBy, findKey, omit, isEqual, merge, isNil } from 'lodash';
import { LayoutService } from '@components/layout/layout.service';
import { AlbumService } from '@components/album/album.service';
import { TeacherFilterService } from '@services/filter/teacher-filter.service';
import { DeliveredAlbum } from '@components/album/teacher/delivered-albums-list/delivered-album';
import { checkBlankString } from '@functions/check-string';
import { SearchConditionSavingService } from '@services/search-condition-saving.service';
import { PageEvent } from '@classi/spen';

@Component({
  selector: 'app-delivered-albums-list',
  templateUrl: './delivered-albums-list.component.html',
  styleUrls: ['./delivered-albums-list.component.scss'],
  standalone: false,
})
export class DeliveredAlbumsListComponent implements OnInit {
  private formBuilder = inject(UntypedFormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private albumService = inject(AlbumService);
  layoutService = inject(LayoutService);
  filterService = inject(TeacherFilterService);
  private searchConditionSavingService = inject(SearchConditionSavingService);

  contentLoaded = false;
  form: UntypedFormGroup;

  iconSort = {
    none: 'sort-column none',
    loader: 'sort-loader fa fa-spinner fa-spin',
    up: 'sort-column fa fa-caret-up',
    down: 'sort-column fa fa-caret-down',
  };

  sortStatus = {
    created_at: this.iconSort.none,
  };

  inProgressSort = false;
  sortKey = null;
  sortOrder = null;
  queryParams = {};
  private readonly allowedParamKeys = ['page', 'keyword', 'distributor', 'sort_key', 'sort_order'];
  private readonly disallowedUrlParamKeys = ['keyword'];

  delivered_albums: DeliveredAlbum[] = [];
  readonly page = signal({
    current_page: 1,
    next_page: null,
    per_page: 1,
    prev_page: null,
    total_count: 1,
    total_pages: 1,
  });

  sortMap = {
    '配信日時（降順）': { sort_key: 'created_at', sort_order: 'desc' },
    '配信日時（昇順）': { sort_key: 'created_at', sort_order: 'asc' },
  };

  distributorOptions = [
    { formValue: null, text: 'すべて' },
    { formValue: 'myself', text: '自分' },
    { formValue: 'other_teachers', text: '自分以外の先生' },
  ];

  isShowFilter = false;
  isSearching = false;
  messageSort: string;

  readonly saveConditionKeys = ['distributor', 'sort_key', 'sort_order'];

  ngOnInit(): void {
    this.loadList(this.route.snapshot.queryParams);
  }

  private loadList(params: Params): void {
    if (this.inProgressSort === false) {
      this.contentLoaded = false;
    }

    const url = this.router.url.split('?')[0];
    let newParams: Params;
    if (Object.keys(params).length === 0) {
      newParams = this.searchConditionSavingService.getSearchConditions(url);
    } else {
      newParams = params;
      const saveConditions = pick(params, this.saveConditionKeys);
      if (!this.layoutService.isTabletDownView.value) {
        delete saveConditions['sort_key'];
        delete saveConditions['sort_order'];
      }
      this.searchConditionSavingService.saveSearchConditions(url, saveConditions);
    }

    this.queryParams = pick(newParams, this.allowedParamKeys);
    this.filterService.setFilter(this.queryParams);
    this.sortOrder = this.queryParams['sort_order'];
    this.sortKey = this.queryParams['sort_key'];
    this.buildForm();
    this.loadDeliveredAlbumList(this.queryParams);
  }

  private navigate(params: Params, paramsHandling?: QueryParamsHandling): void {
    const apiParams = pickBy(params, (value) => !isNil(value));
    this.loadList(apiParams);

    const urlParams = omit(params, this.disallowedUrlParamKeys);
    const extras: NavigationExtras = { queryParams: urlParams, relativeTo: this.route, replaceUrl: true };
    if (paramsHandling !== undefined) {
      extras.queryParamsHandling = paramsHandling;
    }
    this.router.navigate(['.'], extras);
  }

  buildForm() {
    this.messageSort = this.getSortValue();
    this.form = this.formBuilder.group({
      keyword: [this.queryParams['keyword'] || '', Validators.maxLength(50)],
      distributor: [this.queryParams['distributor']],
      sort_value: [this.messageSort],
    });
    this.isSearching = !this.isDefaultForm;
  }

  loadDeliveredAlbumList(params: {}) {
    if (!this.contentLoaded) {
      this.inProgressSort = true;
    }
    this.albumService.getTeacherDeliveredAlbums(params).subscribe(
      (response) => {
        this.page.set({ ...this.page(), ...response.meta });
        this.refreshIconSort();
        this.delivered_albums = response.delivered_albums;
        this.contentLoaded = true;
      },
      (error) => {
        // TODO: add error handling.
      },
    );
  }

  defaultFilterValues() {
    const filterValues = {
      distributor: null,
      keyword: '',
    };

    if (this.layoutService.isTabletDownView.value) {
      filterValues['sort_value'] = Object.keys(this.sortMap)[0];
    }

    return filterValues;
  }

  get isLoadingData(): boolean {
    return !this.contentLoaded || this.inProgressSort;
  }

  onClickAlbum(album: DeliveredAlbum) {
    this.router.navigateByUrl(`albums/teacher/delivery/${album.id}`);
  }

  onClickSpaceName(spaceId: number) {
    this.router.navigateByUrl(`spaces/teacher/${spaceId}`);
  }

  get showPagination(): boolean {
    return this.page().total_pages > 1;
  }

  pageChanged({ index }: PageEvent) {
    const params = merge(this.queryParams, { page: index + 1 });
    this.navigate(params, 'merge');
    window.scrollTo(0, 0);
  }

  changeBySortColumn(sortKey: string) {
    if (this.inProgressSort) {
      return false;
    }
    this.inProgressSort = true;

    if (sortKey === this.sortKey) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortStatus[this.sortKey] = this.iconSort.none;
      this.sortOrder = 'desc';
      this.sortKey = sortKey;
    }
    this.sortStatus[this.sortKey] = this.iconSort.loader;

    const params = merge(this.queryParams, this.buildSortParams());
    this.navigate(params, 'merge');
  }

  buildSortParams() {
    return {
      page: 1,
      sort_key: this.sortKey,
      sort_order: this.sortOrder,
    };
  }

  refreshIconSort() {
    this.sortKey = this.sortKey || 'created_at';
    const icon = this.sortOrder === 'asc' ? 'up' : 'down';
    this.sortStatus[this.sortKey] = this.iconSort[icon];
    this.inProgressSort = false;
  }

  changeShowFilterStatus() {
    this.isShowFilter = !this.isShowFilter;
  }

  getSortValue() {
    const formValue = findKey(this.sortMap, pick(this.queryParams, ['sort_key', 'sort_order']));
    return formValue || Object.keys(this.sortMap)[0];
  }

  removeFilter() {
    if (this.layoutService.isTabletDownView.value) {
      const sortDefault = Object.keys(this.sortMap)[0];
      this.queryParams['sort_value'] = sortDefault;
      Object.assign(this.queryParams, this.sortMap[sortDefault]);
    }

    this.form.reset(this.defaultFilterValues());
    this.onSubmitFilter();
  }

  onSubmitFilter() {
    window.scrollTo(0, 0);
    this.isShowFilter = false;
    this.navigate(this.buildFilterParams());
  }

  buildFilterParams() {
    const params = pickBy(this.form.value);
    if (checkBlankString(params['keyword'])) {
      delete params['keyword'];
      this.form.controls['keyword'].setValue('');
    } else {
      params['keyword'] = params['keyword'].trim();
      this.form.controls['keyword'].setValue(params['keyword']);
    }

    params['request_timestamp'] = Date.now();

    return Object.assign(omit(params, ['sort_value']), this.sortMap[this.getSortValue()]);
  }

  onChangeSortValue(value: string) {
    this.queryParams['sort_value'] = value;
    Object.assign(this.queryParams, this.sortMap[value]);
  }

  get isDefaultForm(): boolean {
    const compareValues = this.form.value;

    if (!this.layoutService.isTabletDownView.value) {
      delete compareValues['sort_value'];
    }

    return isEqual(compareValues, this.defaultFilterValues());
  }
}
